import React, { useMemo, useState, useEffect } from "react";
import Breadcrumbs from "../../../Components/Common/Breadcrumb";
import TableContainer from "../../../Components/Common/TableContainer";
import {
  Modal,
  ModalHeader,
  ModalBody,
  Card,
  CardBody,
  Row,
  Col,
  Alert,
  Badge,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  CardTitle,
} from "reactstrap";
import axios from "axios";

const baseUrl = process.env.REACT_APP_BASE_URL;

interface column {
  header: string;
  accessorKey?: string;
  accessorFn?: (row: any) => string;
  enableColumnFilter: boolean;
  enableSorting: boolean;
  cell?: (info: any) => React.ReactNode;
}

const AdminFeedback = () => {
  const [showloader, setshowloader] = useState(true);
  const [alertMsgg, setAlertMsgg] = useState<any>({
    open: false,
    type: "success",
    content: "Messages here.",
    time: "2500",
  });

  const [submissions, setSubmissions] = useState<any[]>([]);
  const [selectedSubmission, setSelectedSubmission] = useState<any>(null);
  const [detailModal, setDetailModal] = useState(false);
  const [updateModal, setUpdateModal] = useState(false);
  const [dashboardStats, setDashboardStats] = useState<any>(null);

  const [filters, setFilters] = useState({
    status: "",
    priority: "",
  });

  const [updateData, setUpdateData] = useState({
    status: "",
    priority: "",
    admin_notes: "",
  });

  document.title = "Feedback Management | BizServe Admin";

  const handleAlertMsggClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setAlertMsgg({ ...alertMsgg, open: false });
  };

  // Fetch all feedback submissions
  const fetchSubmissions = async () => {
    try {
      setshowloader(true);
      const params = new URLSearchParams();
      if (filters.status) params.append("status", filters.status);
      if (filters.priority) params.append("priority", filters.priority);

      const response = await axios.get(
        `${baseUrl}/feedback/list/?${params.toString()}`,
        { withCredentials: true }
      );
    
      const submissionsData = response.submissions || [];
      setSubmissions(Array.isArray(submissionsData) ? submissionsData : []);
      setshowloader(false);
    } catch (error: any) {
      console.error("Error fetching submissions:", error);
      setSubmissions([]);
      // Don't show error alert if it's just empty data
      if (error.response && error.response.status !== 200) {
        setAlertMsgg({
          open: true,
          content: "Error loading feedback submissions",
          type: "danger",
          time: "3000",
        });
      }
      setshowloader(false);
    }
  };

  // Fetch dashboard statistics
  const fetchDashboardStats = async () => {
    try {
      const response = await axios.get(
        `${baseUrl}/feedback/stats/`,
        { withCredentials: true }
      );
      setDashboardStats(response.stats || response);
    } catch (error: any) {
      console.error("Error fetching dashboard stats:", error);
      // Set default stats if error
      setDashboardStats({
        total: 0,
        pending_review: 0,
        avg_rating: null,
        by_status: {},
        by_priority: {},
        recent: []
      });
    }
  };

  // Fetch submission details
  const fetchSubmissionDetail = async (id: number) => {
    try {
      const response = await axios.get(
        `${baseUrl}/feedback/detail/${id}/`,
        { withCredentials: true }
      );
      const submission = response.submission || response;
      setSelectedSubmission(submission);
      setUpdateData({
        status: submission.status,
        priority: submission.priority || "medium",
        admin_notes: submission.admin_notes || "",
      });
    } catch (error: any) {
      console.error("Error fetching submission detail:", error);
      setAlertMsgg({
        open: true,
        content: "Error loading feedback details",
        type: "danger",
        time: "3000",
      });
    }
  };

  // Update submission
  const handleUpdateSubmission = async () => {
    try {
      const response = await axios.patch(
        `${baseUrl}/feedback/update/${selectedSubmission.id}/`,
        updateData,
        { withCredentials: true }
      );

      if (response.status === 200) {
        setAlertMsgg({
          open: true,
          content: "Feedback updated successfully",
          type: "success",
          time: "2500",
        });
        setUpdateModal(false);
        fetchSubmissions();
        if (detailModal) {
          fetchSubmissionDetail(selectedSubmission.id);
        }
      }
    } catch (error: any) {
      console.error("Error updating submission:", error);
      setAlertMsgg({
        open: true,
        content: "Error updating feedback",
        type: "danger",
        time: "3000",
      });
    }
  };

  useEffect(() => {
    fetchSubmissions();
    fetchDashboardStats();
  }, [filters]);

  const toggleDetailModal = () => {
    setDetailModal(!detailModal);
    if (detailModal) {
      setSelectedSubmission(null);
    }
  };

  const toggleUpdateModal = () => {
    setUpdateModal(!updateModal);
  };

  const viewDetails = async (submission: any) => {
    await fetchSubmissionDetail(submission.id);
    setDetailModal(true);
  };

  const openUpdateModal = async (submission: any) => {
    await fetchSubmissionDetail(submission.id);
    setUpdateModal(true);
  };

  // Status badge color
  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case "submitted":
        return "info";
      case "under_review":
        return "warning";
      case "resolved":
        return "success";
      case "archived":
        return "secondary";
      default:
        return "light";
    }
  };

  // Priority badge color
  const getPriorityBadgeColor = (priority: string) => {
    switch (priority) {
      case "critical":
        return "danger";
      case "high":
        return "warning";
      case "medium":
        return "info";
      case "low":
        return "secondary";
      default:
        return "light";
    }
  };

  // Table columns
  const columns: column[] = useMemo(
    () => [
      {
        header: "ID",
        accessorKey: "id",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Customer Name",
        accessorKey: "customer_name_display",
        enableColumnFilter: true,
        enableSorting: true,
        cell: (info: any) => (
          <div className="text-capitalize">
            {info.getValue()}
          </div>
        ),
      },
      {
        header: "Email",
        accessorKey: "customer_email_display",
        enableColumnFilter: true,
        enableSorting: true,
      },
      {
        header: "Phone",
        accessorKey: "customer_phone_display",
        enableColumnFilter: true,
        enableSorting: true,
      },
      // {
      //   header: "Status",
      //   accessorKey: "status",
      //   enableColumnFilter: false,
      //   enableSorting: true,
      //   cell: (info: any) => (
      //     <Badge color={getStatusBadgeColor(info.getValue())} className="text-capitalize" pill>
      //       {info.getValue()}
      //     </Badge>
      //   ),
      // },
      // {
      //   header: "Priority",
      //   accessorKey: "priority",
      //   enableColumnFilter: false,
      //   enableSorting: true,
      //   cell: (info: any) => (
      //     <Badge color={getPriorityBadgeColor(info.getValue())} className="text-capitalize" pill>
      //       {info.getValue() || "N/A"}
      //     </Badge>
      //   ),
      // },
      {
        header: "Submitted",
        accessorKey: "submitted_at",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (info: any) =>
          new Date(info.getValue()).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
          }),
      },
      {
        header: "Actions",
        accessorKey: "id",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (info: any) => (
          <div className="d-flex gap-2">
            <Button
              color="info"
              size="sm"
              onClick={() => viewDetails(info.row.original)}
            >
              <i className="mdi mdi-eye"></i>
            </Button>
            {/* <Button
              color="primary"
              size="sm"
              onClick={() => openUpdateModal(info.row.original)}
            >
              <i className="mdi mdi-pencil"></i>
            </Button> */}
          </div>
        ),
      },
    ],
    []
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Admin" breadcrumbItem="Feedback Management" />

          {alertMsgg.open && (
            <Alert
              color={alertMsgg.type}
              isOpen={alertMsgg.open}
              toggle={handleAlertMsggClose}
            >
              {alertMsgg.content}
            </Alert>
          )}

          {/* Dashboard Stats */}
          {dashboardStats && (
            <Row className="mb-4">
              <Col md="4">
                <Card className="mini-stats-wid">
                  <CardBody>
                    <div className="d-flex">
                      <div className="flex-grow-1">
                        <p className="text-muted fw-medium">Total Feedback</p>
                        <h4 className="mb-0">{dashboardStats.total}</h4>
                      </div>
                      <div className="avatar-sm rounded-circle bg-primary align-self-center mini-stat-icon">
                        <span className="avatar-title rounded-circle bg-primary">
                          <i className="bx bx-message-dots font-size-24"></i>
                        </span>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
              {/* <Col md="4">
                <Card className="mini-stats-wid">
                  <CardBody>
                    <div className="d-flex">
                      <div className="flex-grow-1">
                        <p className="text-muted fw-medium">Pending Review</p>
                        <h4 className="mb-0">{dashboardStats.pending_review}</h4>
                      </div>
                      <div className="avatar-sm rounded-circle bg-warning align-self-center mini-stat-icon">
                        <span className="avatar-title rounded-circle bg-warning">
                          <i className="bx bx-hourglass font-size-24"></i>
                        </span>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
              <Col md="4">
                <Card className="mini-stats-wid">
                  <CardBody>
                    <div className="d-flex">
                      <div className="flex-grow-1">
                        <p className="text-muted fw-medium">Resolved</p>
                        <h4 className="mb-0">
                          {dashboardStats.by_status?.resolved || 0}
                        </h4>
                      </div>
                      <div className="avatar-sm rounded-circle bg-info align-self-center mini-stat-icon">
                        <span className="avatar-title rounded-circle bg-info">
                          <i className="bx bx-check-circle font-size-24"></i>
                        </span>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col> */}
            </Row>
          )}

          {/* Filters */}
          {/* <Card>
            <CardBody>
              <CardTitle>Filter Feedback</CardTitle>
              <Row>
                <Col md="3">
                  <FormGroup>
                    <Label>Status</Label>
                    <Input
                      type="select"
                      value={filters.status}
                      onChange={(e) =>
                        setFilters({ ...filters, status: e.target.value })
                      }
                    >
                      <option value="">All Statuses</option>
                      <option value="submitted">Submitted</option>
                      <option value="under_review">Under Review</option>
                      <option value="resolved">Resolved</option>
                      <option value="archived">Archived</option>
                    </Input>
                  </FormGroup>
                </Col>
                <Col md="3">
                  <FormGroup>
                    <Label>Priority</Label>
                    <Input
                      type="select"
                      value={filters.priority}
                      onChange={(e) =>
                        setFilters({ ...filters, priority: e.target.value })
                      }
                    >
                      <option value="">All Priorities</option>
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="critical">Critical</option>
                    </Input>
                  </FormGroup>
                </Col>
                <Col md="3">
                  <Label>&nbsp;</Label>
                  <div>
                    <Button
                      color="secondary"
                      onClick={() =>
                        setFilters({ status: "", priority: "" })
                      }
                    >
                      Clear Filters
                    </Button>
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card> */}

          {/* Submissions Table */}
          <Card>
            <CardBody>
              <CardTitle className="mb-4">Feedback Submissions</CardTitle>
              {showloader ? (
                <div className="text-center">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : (
                <TableContainer
                  columns={columns}
                  data={submissions}
                  isGlobalFilter={true}
                  isPagination={true}
                  pageSize={10}
                  tableClass="table-striped table-bordered"
                />
              )}
            </CardBody>
          </Card>
        </div>
      </div>

      {/* Detail Modal */}
      <Modal
        isOpen={detailModal}
        toggle={toggleDetailModal}
        size="lg"
        centered
      >
        <ModalHeader toggle={toggleDetailModal}>
          Feedback Details - #{selectedSubmission?.id}
        </ModalHeader>
        <ModalBody>
          {selectedSubmission && (
            <div>
              {/* Customer Info */}
              <Card className="mb-3">
                <CardBody>
                  <h5 className="card-title">Customer Information</h5>
                  <Row>
                    <Col md="6">
                      <p>
                        <strong>Name:</strong>{" "}
                        {selectedSubmission.customer_name_display}
                      </p>
                      <p>
                        <strong>Email:</strong>{" "}
                        {selectedSubmission.customer_email_display}
                      </p>
                    </Col>
                    <Col md="6">
                      <p>
                        <strong>Phone:</strong>{" "}
                        {selectedSubmission.customer_phone_display}
                      </p>
                      <p>
                        <strong>Submitted:</strong>{" "}
                        {new Date(
                          selectedSubmission.submitted_at
                        ).toLocaleString()}
                      </p>
                    </Col>
                  </Row>
                </CardBody>
              </Card>

              {/* Feedback Details */}
              {/* <Card className="mb-3">
                <CardBody>
                  <h5 className="card-title">Feedback Details</h5>
                  <p>
                    <strong>Status:</strong>{" "}
                    <Badge color={getStatusBadgeColor(selectedSubmission.status)}>
                      {selectedSubmission.status}
                    </Badge>
                  </p>
                  <p>
                    <strong>Priority:</strong>{" "}
                    <Badge
                      color={getPriorityBadgeColor(selectedSubmission.priority)}
                    >
                      {selectedSubmission.priority || "N/A"}
                    </Badge>
                  </p>
                </CardBody>
              </Card> */}

              {/* Form Responses */}
              <Card className="mb-3">
                <CardBody>
                  <h5 className="card-title">Form Responses</h5>
                  {selectedSubmission.responses && Array.isArray(selectedSubmission.responses) ? (
                    selectedSubmission.responses.map((item: any, idx: number) => (
                      <div key={idx} className="mb-3">
                        <strong>{item.question}:</strong>
                        <p className="mb-0">{item.answer || "N/A"}</p>
                      </div>
                    ))
                  ) : (
                    <p className="text-muted">No responses available</p>
                  )}
                </CardBody>
              </Card>

              {/* Admin Notes */}
              {selectedSubmission.admin_notes && (
                <Card className="mb-3">
                  <CardBody>
                    <h5 className="card-title">Admin Notes</h5>
                    <p>{selectedSubmission.admin_notes}</p>
                  </CardBody>
                </Card>
              )}

              <div className="d-flex gap-2">
                {/* <Button color="primary" onClick={() => {
                  toggleDetailModal();
                  openUpdateModal(selectedSubmission);
                }}>
                  Update Feedback
                </Button> */}
                <Button color="secondary" onClick={toggleDetailModal}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </ModalBody>
      </Modal>

      {/* Update Modal */}
      <Modal isOpen={updateModal} toggle={toggleUpdateModal} centered>
        <ModalHeader toggle={toggleUpdateModal}>
          Update Feedback - #{selectedSubmission?.id}
        </ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label>Status</Label>
              <Input
                type="select"
                value={updateData.status}
                onChange={(e) =>
                  setUpdateData({ ...updateData, status: e.target.value })
                }
              >
                <option value="submitted">Submitted</option>
                <option value="under_review">Under Review</option>
                <option value="resolved">Resolved</option>
                <option value="archived">Archived</option>
              </Input>
            </FormGroup>
            <FormGroup>
              <Label>Priority</Label>
              <Input
                type="select"
                value={updateData.priority}
                onChange={(e) =>
                  setUpdateData({ ...updateData, priority: e.target.value })
                }
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </Input>
            </FormGroup>
            <FormGroup>
              <Label>Admin Notes</Label>
              <Input
                type="textarea"
                rows={4}
                value={updateData.admin_notes}
                onChange={(e) =>
                  setUpdateData({ ...updateData, admin_notes: e.target.value })
                }
                placeholder="Add internal notes about this feedback..."
              />
            </FormGroup>
            <div className="d-flex gap-2">
              <Button color="primary" onClick={handleUpdateSubmission}>
                Save Changes
              </Button>
              <Button color="secondary" onClick={toggleUpdateModal}>
                Cancel
              </Button>
            </div>
          </Form>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default AdminFeedback;

